import { useCallback, useEffect, useRef, useState } from "react";

import getToken from "utils/getToken";
import {
  redirectToLogin,
  triggerSessionExpiry,
  cancelSessionExpiry,
} from "utils/auth";
import { parseJWT } from "utils/currentUser.util";
import { refreshToken } from "services/auth.service";

import {
  MAX_SAFE_SETTIMEOUT_DELAY_MS,
  SESSION_EXPIRY_BROADCAST_KEY,
  SESSION_EXPIRY_WARNING_TIME_MS,
} from "static/constants";

const useSessionExpiry = () => {
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const timeoutIdsRef = useRef([]);

  const clearScheduledTimers = useCallback(() => {
    timeoutIdsRef.current.forEach(clearTimeout);
    timeoutIdsRef.current = [];
  }, []);

  const expireSession = useCallback(() => {
    triggerSessionExpiry();
  }, []);

  const scheduleTimeout = useCallback((callback, delay) => {
    if (delay > MAX_SAFE_SETTIMEOUT_DELAY_MS) {
      timeoutIdsRef.current.push(
        setTimeout(
          () => scheduleTimeout(callback, delay - MAX_SAFE_SETTIMEOUT_DELAY_MS),
          MAX_SAFE_SETTIMEOUT_DELAY_MS
        )
      );
    } else {
      timeoutIdsRef.current.push(setTimeout(callback, delay));
    }
  }, []);

  const scheduleFromToken = useCallback(
    (token) => {
      clearScheduledTimers();

      if (!token) {
        return;
      }

      const payload = parseJWT(token);
      const expiryMs = payload?.exp ? payload.exp * 1000 : null;

      if (expiryMs === null) {
        return;
      }

      const msUntilExpiry = expiryMs - Date.now();

      if (msUntilExpiry <= 0) {
        expireSession();
        return;
      }

      const msUntilWarning = msUntilExpiry - SESSION_EXPIRY_WARNING_TIME_MS;

      if (msUntilWarning <= 0) {
        setShowExpiryWarning(true);
      } else {
        scheduleTimeout(() => setShowExpiryWarning(true), msUntilWarning);
      }

      scheduleTimeout(expireSession, msUntilExpiry);
    },
    [clearScheduledTimers, expireSession, scheduleTimeout]
  );

  useEffect(() => {
    scheduleFromToken(getToken());
    return clearScheduledTimers;
  }, [scheduleFromToken, clearScheduledTimers]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === SESSION_EXPIRY_BROADCAST_KEY && event.newValue) {
        triggerSessionExpiry(undefined, { silent: true });
        return;
      }

      if (event.key !== "authToken") {
        return;
      }

      setShowExpiryWarning(false);
      scheduleFromToken(event.newValue);

      if (!event.newValue) {
        redirectToLogin(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [scheduleFromToken]);

  const extendSession = useCallback(async () => {
    setIsExtending(true);

    try {
      const { data } = await refreshToken();
      const newToken = data?.token;

      if (!newToken) {
        throw new Error("Session refresh did not return a token");
      }

      localStorage.setItem("authToken", newToken);
      setShowExpiryWarning(false);
      cancelSessionExpiry();
      scheduleFromToken(newToken);
    } catch (error) {
      clearScheduledTimers();
      triggerSessionExpiry(error?.message || "Failed to extend session", {
        priority: true,
      });
    } finally {
      setIsExtending(false);
    }
  }, [scheduleFromToken, clearScheduledTimers]);

  return {
    isExtending,
    extendSession,
    showExpiryWarning,
  };
};

export default useSessionExpiry;
